import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MagnifyingGlass, SignOut, Spinner, DownloadSimple, CaretRight, Package, Folder,
} from "@phosphor-icons/react";
import { fetchCompressors, compressorsExportUrl } from "@/lib/api";
import SortableTh, { nextSort, compareBy } from "@/components/SortableTh";
import CompressorFilesModal from "@/components/CompressorFilesModal";
import CompressorDetailModal from "@/components/CompressorDetailModal";
import { useT } from "@/lib/i18n";

const BRAND_META = {
  US: {
    key: "US",
    name: "US Air Compressor",
    logo: "https://usaircompressor.com/wp-content/uploads/2024/09/logo_reduced.webp",
    accent: "border-l-blue-600",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
    domain: "usaircompressor.com",
  },
  ACC: {
    key: "ACC",
    name: "Air Compressor Canada",
    logo: "https://aircompressorcanada.com/wp-content/uploads/2026/04/acc_black.png",
    accent: "border-l-red-600",
    badge: "bg-red-50 text-red-700 border-red-200",
    domain: "aircompressorcanada.com",
  },
};

function fmtHours(v) {
  if (v == null || v === "" || isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("en-US");
}
function fmtDate(d) {
  if (!d) return "—";
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  try { return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); } catch { return s; }
}

const STATUS_ROW = {
  overdue: "border-l-4 border-l-red-500 bg-red-50/40",
  due_soon: "border-l-4 border-l-amber-500 bg-amber-50/40",
  normal: "border-l-4 border-l-transparent",
};
const STATUS_TEXT = {
  overdue: "text-red-700 font-bold",
  due_soon: "text-amber-700 font-semibold",
  normal: "text-slate-700",
};
const STATUS_DOT = {
  overdue: "bg-red-500",
  due_soon: "bg-amber-500",
  normal: "bg-emerald-500",
};

export default function CompressorsListPage({ user, onLogout }) {
  const tx = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [total, setTotal] = useState(0);
  // req #10 (iter110): US ↔ ACC are mutually exclusive — selecting one
  // closes the other. Default to US. The dealer-fleet view never wants to
  // mix the two product lines side-by-side.
  const [activeBrand, setActiveBrand] = useState("US");
  // Per-column sort state — default: Company (owner) ascending, as requested.
  const [sortKey, setSortKey] = useState("owner_company_name");
  const [sortDir, setSortDir] = useState("asc");
  const handleSort = (col) => {
    const { key, dir } = nextSort(sortKey, sortDir, col);
    setSortKey(key); setSortDir(dir);
  };

  const isCustomer = user?.role === "customer";
  const heading = isCustomer ? "My Products" : "Products Sold";

  useEffect(() => {
    setLoading(true); setErr("");
    const t = setTimeout(() => {
      fetchCompressors({ search, per_page: 2000, sort_by: sortKey || "owner_company_name", sort_dir: sortDir })
        .then((d) => { setItems(d.items || []); setTotal(d.total || 0); })
        .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, sortKey, sortDir]);

  const counts = useMemo(() => {
    const out = { overdue: 0, due_soon: 0, normal: 0 };
    for (const c of items) out[c.service_status] = (out[c.service_status] || 0) + 1;
    return out;
  }, [items]);

  const filtered = useMemo(() => (
    statusFilter === "all" ? items : items.filter((c) => c.service_status === statusFilter)
  ), [items, statusFilter]);

  // Group by brand; only show brands the user actually has products in.
  const brandGroups = useMemo(() => {
    const groups = { US: [], ACC: [] };
    for (const c of filtered) {
      const b = c.brand === "ACC" ? "ACC" : "US";
      groups[b].push(c);
    }
    if (sortKey) {
      const mult = sortDir === "desc" ? -1 : 1;
      const sorter = (a, b) => mult * compareBy(a, b, sortKey);
      groups.US.sort(sorter);
      groups.ACC.sort(sorter);
    }
    return groups;
  }, [filtered, sortKey, sortDir]);
  const availableBrands = useMemo(() => {
    // Compute from the unfiltered item list so toggling a status filter doesn't
    // make a folder vanish entirely — we always show folders the user owns.
    const set = new Set();
    for (const c of items) set.add(c.brand === "ACC" ? "ACC" : "US");
    return Array.from(set);
  }, [items]);

  // Selecting a brand closes the other (mutually exclusive — req #10).
  const selectBrand = (b) => setActiveBrand(b);

  // If the user owns only one brand — or the current activeBrand isn't
  // in the available list (e.g. status filter just hid all of them) —
  // auto-pick the first available so the list isn't blank.
  useEffect(() => {
    if (availableBrands.length === 0) return;
    if (!availableBrands.includes(activeBrand)) {
      setActiveBrand(availableBrands[0]);
    }
  }, [availableBrands, activeBrand]);

  const [exporting, setExporting] = useState("");
  const [filesFor, setFilesFor] = useState(null);

  // ─── Compressor detail modal ───
  // Opening a row (or arriving via deep-link `/compressors/:serialId`) sets
  // ``openCompressor`` to that row. The CompressorDetailModal then loads the
  // full forecast / PM / parts / phone-logs in one go — same UX as on the
  // CustomerPage. URL is kept in sync via history.replaceState so deep-link
  // sharing + back-button "close modal" both work.
  const navigate = useNavigate();
  const { serialId: deepLinkSerial } = useParams();
  const [openCompressor, setOpenCompressor] = useState(null);

  // Open from URL on first paint (covers /compressors/:serialId deep-link
  // from emails / WarrantyClaimPage / Service-Tech).
  useEffect(() => {
    if (!deepLinkSerial) return;
    if (openCompressor && openCompressor.serial_id === deepLinkSerial) return;
    if (loading) return;  // wait for items to load so we can match
    const match = items.find((c) => c.serial_id === deepLinkSerial);
    if (match) {
      setOpenCompressor(match);
    } else {
      // Stub if not in our visible list — modal still works on serialId alone.
      setOpenCompressor({ serial_id: deepLinkSerial, brand: "US", owner_customer_id: "" });
    }
  }, [deepLinkSerial, items, loading, openCompressor]);

  const openDetail = useCallback((c) => {
    setOpenCompressor(c);
    // Deep-link friendly URL — back button closes the modal naturally.
    try { window.history.pushState({}, "", `/compressors/${encodeURIComponent(c.serial_id)}`); } catch { /* ignore */ }
  }, []);

  const closeDetail = useCallback(() => {
    setOpenCompressor(null);
    try { window.history.pushState({}, "", "/compressors"); } catch { /* ignore */ }
  }, []);

  const handleExport = async (brand) => {
    if (exporting) return;
    setExporting(brand);
    try {
      const url = compressorsExportUrl({
        brand,
        search,
        status: statusFilter === "all" ? "" : statusFilter,
        sort_by: sortKey,
        sort_dir: sortDir,
      });
      const headers = {};
      if (user?.email) headers["X-User-Email"] = user.email;
      const res = await fetch(url, { credentials: "include", headers });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `compressors_${brand.toLowerCase()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    } catch (e) {
      setErr(e?.message || "Export failed");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="compressors-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to={isCustomer ? "/my-account" : "/"}
          className="text-slate-500 hover:text-blue-700 flex items-center gap-1 text-sm font-medium"
          data-testid="compressors-back">
          <ArrowLeft size={16} /> Home
        </Link>
        <div className="flex items-center gap-1.5 ml-1">
          <Package size={18} weight="bold" className="text-blue-700" />
          <h1 className="text-lg md:text-xl font-bold font-['Chivo'] text-slate-900" data-testid="compressors-heading">
            {heading}
          </h1>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden md:block text-[11px] text-slate-500">{user?.name || user?.email}</div>
          <button onClick={onLogout} data-testid="compressors-logout"
            className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400 hover:text-slate-600" title={tx("compressor_page.sign_out", "Sign out")}>
            <SignOut size={15} />
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-3 md:px-6 py-5 space-y-4">
        {/* Status chips + search */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5 flex-wrap">
            <Chip testId="filter-all" label={tx("compressors_list.filter_all", "All")} count={items.length} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} dot="bg-slate-400" />
            <Chip testId="filter-overdue" label={tx("compressors_list.filter_overdue", "Overdue")} count={counts.overdue || 0} active={statusFilter === "overdue"} onClick={() => setStatusFilter("overdue")} dot="bg-red-500" />
            <Chip testId="filter-due-soon" label={tx("compressors_list.filter_due_soon", "Due soon")} count={counts.due_soon || 0} active={statusFilter === "due_soon"} onClick={() => setStatusFilter("due_soon")} dot="bg-amber-500" />
            <Chip testId="filter-normal" label={tx("compressors_list.filter_ok", "OK")} count={counts.normal || 0} active={statusFilter === "normal"} onClick={() => setStatusFilter("normal")} dot="bg-emerald-500" />
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-md ml-auto">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="compressors-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={tx("compressors_list.search_placeholder", "Serial #, product, company…")}
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">
            {filtered.length}{statusFilter !== "all" ? ` / ${items.length}` : ""}
          </span>
        </div>

        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="compressors-error">{err}</div>}
        {loading && <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="compressors-loading"><Spinner size={14} className="animate-spin" /> Loading…</div>}

        {!loading && !err && availableBrands.length === 0 && (
          <div className="text-sm text-slate-500 text-center py-10 border border-dashed border-slate-200 rounded-sm" data-testid="compressors-empty">
            {isCustomer ? "No compressors registered under your account." : "No compressors match this view."}
          </div>
        )}

        {/* req #10 (iter110): Brand picker — US / ACC are MUTUALLY exclusive.
            User can only view one brand's compressors at a time. The list
            below renders only the active brand's folder. */}
        {!loading && !err && availableBrands.length > 1 && (
          <div className="flex items-center gap-1.5" data-testid="brand-picker">
            {availableBrands.map((b) => {
              const meta = BRAND_META[b];
              const isActive = activeBrand === b;
              return (
                <button
                  key={b}
                  onClick={() => selectBrand(b)}
                  data-testid={`brand-pick-${b}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-sm border transition ${isActive ? `${meta.badge} border-current` : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
                >
                  {meta.name}
                  <span className={`text-[9px] font-bold ${isActive ? "" : "text-slate-400"}`}>
                    ({items.filter((c) => (c.brand === "ACC" ? "ACC" : "US") === b).length})
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Folder section for the ACTIVE brand only */}
        {!loading && !err && availableBrands.includes(activeBrand) && (() => {
          const brand = activeBrand;
          const meta = BRAND_META[brand];
          const rows = brandGroups[brand] || [];
          const open = true;
          const totalBrand = items.filter((c) => (c.brand === "ACC" ? "ACC" : "US") === brand).length;
          return (
            <section key={brand} className={`bg-white border border-slate-200 rounded-sm border-l-4 ${meta.accent}`}
              data-testid={`brand-folder-${brand}`}>
              {/* Folder header */}
              <header className="flex items-center gap-3 px-3 md:px-4 py-3 bg-slate-50/80 border-b border-slate-200">
                <img src={meta.logo} alt={meta.name} loading="lazy"
                  className="h-8 md:h-9 w-auto max-w-[140px] object-contain shrink-0"
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm md:text-base font-bold font-['Chivo'] text-slate-900 truncate">{meta.name}</div>
                  <div className="text-[10px] text-slate-500">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${meta.badge} font-bold uppercase tracking-wider mr-2`}>{brand}</span>
                    {rows.length}{statusFilter !== "all" ? ` shown / ${totalBrand} total` : " products"}
                  </div>
                </div>
                <button onClick={() => handleExport(brand)}
                  data-testid={`brand-export-${brand}`}
                  disabled={exporting === brand}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700 disabled:opacity-50 disabled:cursor-wait">
                  {exporting === brand ? <Spinner size={13} className="animate-spin" /> : <DownloadSimple size={13} weight="bold" />}
                  Excel
                </button>
              </header>

              {open && (
                <>
                  {rows.length === 0 ? (
                    <div className="text-xs text-slate-500 text-center py-6" data-testid={`brand-empty-${brand}`}>
                      {tx("compressors_list.no_match", "No products match the current filter.")}
                    </div>
                  ) : (
                    <>
                      {/* Desktop table — overflow-visible so the <thead> can stick to the page scroll */}
                      <div className="hidden md:block">
                        <table className="w-full text-sm table-fixed" data-testid={`brand-table-${brand}`}>
                          <thead className="sticky top-[52px] z-10 bg-white border-b border-slate-200 shadow-[0_1px_0_0_rgb(226_232_240)]">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                              <th className="px-3 py-2 w-2 bg-white"></th>
                              <SortableTh column="serial_number" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 bg-white">{tx("table.serial_num", "Serial #")}</SortableTh>
                              <SortableTh column="product_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 bg-white">{tx("table.product", "Product")}</SortableTh>
                              <SortableTh column="owner_company_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 bg-white">{tx("table.company", "Company")}</SortableTh>
                              <SortableTh column="hours" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" className="px-3 py-2 bg-white text-right">{tx("table.hours", "Hours")}</SortableTh>
                              <SortableTh column="next_service_hour" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" className="px-3 py-2 bg-white text-right">{tx("compressors_list.col_next_service", "Next Service")}</SortableTh>
                              <SortableTh column="last_connection" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 bg-white">{tx("compressors_list.col_last_connection", "Last Connection")}</SortableTh>
                              <th className="px-3 py-2 w-16 bg-white text-right text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">{tx("table.files", "Files")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((c) => (
                              <tr key={c.serial_id}
                                onClick={() => openDetail(c)}
                                data-testid={`compressor-row-${c.serial_id}`}
                                data-status={c.service_status}
                                title={tx("compressors_list.row_click_tip", "View full compressor details")}
                                className={`border-b border-slate-100 hover:bg-blue-50/30 cursor-pointer ${STATUS_ROW[c.service_status] || STATUS_ROW.normal}`}>
                                <td className="pl-2 pr-0 py-2">
                                  <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[c.service_status] || STATUS_DOT.normal}`} />
                                </td>
                                <td className="px-3 py-2 font-mono font-semibold">{c.serial_number || "—"}</td>
                                <td className="px-3 py-2">
                                  <div className={`truncate max-w-[220px] ${STATUS_TEXT[c.service_status] || STATUS_TEXT.normal}`} title={c.product_name}>
                                    {c.product_name || "—"}
                                  </div>
                                  {c.service_plan && <div className="text-[10px] text-slate-500 truncate max-w-[220px]" title={c.service_plan}>{c.service_plan}</div>}
                                </td>
                                <td className="px-3 py-2 truncate max-w-[220px]" title={c.owner_company_name}>{c.owner_company_name || "—"}</td>
                                <td className={`px-3 py-2 text-right font-mono ${STATUS_TEXT[c.service_status] || STATUS_TEXT.normal}`}>{fmtHours(c.hours)}</td>
                                <td className="px-3 py-2 text-right font-mono" title={`Base ${c.base_hour ?? 0} + Max ${c.max_hours_allowed ?? 0}`}>
                                  {c.next_service_hour != null ? fmtHours(c.next_service_hour) : "—"}
                                </td>
                                <td className="px-3 py-2 text-[11px] text-slate-500">{fmtDate(c.last_connection)}</td>
                                <td className="px-3 py-2 text-right">
                                  <button onClick={(e) => { e.stopPropagation(); setFilesFor(c); }} data-testid={`compressor-files-btn-${c.serial_id}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-1 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700"
                                    title={tx("compressors_list.view_files_tip", "View pictures & files for this compressor")}>
                                    <Folder size={11} weight="bold" /> Files
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile cards */}
                      <ul className="md:hidden divide-y divide-slate-100">
                        {rows.map((c) => (
                          <li key={c.serial_id}
                            onClick={() => openDetail(c)}
                            data-testid={`compressor-card-${c.serial_id}`}
                            data-status={c.service_status}
                            className={`${STATUS_ROW[c.service_status] || STATUS_ROW.normal} p-3 cursor-pointer active:bg-blue-50`}>
                            <div className="flex items-start gap-2 mb-1">
                              <span className={`mt-1 w-2 h-2 rounded-full ${STATUS_DOT[c.service_status] || STATUS_DOT.normal}`} />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-mono font-bold">{c.serial_number || "—"}</div>
                                <div className={`text-xs font-semibold truncate ${STATUS_TEXT[c.service_status] || STATUS_TEXT.normal}`}>{c.product_name || "—"}</div>
                                <div className="text-[11px] text-slate-500 truncate">{c.owner_company_name || "—"}</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                              <KV k="Hours" v={fmtHours(c.hours)} highlight={STATUS_TEXT[c.service_status]} />
                              <KV k="Next Service" v={c.next_service_hour != null ? fmtHours(c.next_service_hour) : "—"} />
                              <KV k="Last Seen" v={fmtDate(c.last_connection)} />
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); setFilesFor(c); }} data-testid={`compressor-files-btn-mobile-${c.serial_id}`}
                              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700">
                              <Folder size={11} weight="bold" /> View files & pictures
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </section>
          );
        })()}

        <p className="text-[11px] text-slate-400" data-testid="compressors-footnote">
          {total > items.length && `Showing first ${items.length} of ${total} — refine the search to see more.`}
        </p>
      </main>

      {filesFor && <CompressorFilesModal compressor={filesFor} user={user} onClose={() => setFilesFor(null)} />}
      {openCompressor && (
        <CompressorDetailModal
          serialId={openCompressor.serial_id}
          customerId={openCompressor.owner_customer_id || openCompressor.customer_id || ""}
          isCA={openCompressor.brand === "ACC"}
          user={user}
          onClose={closeDetail}
          onOpenInvoice={(hid) => {
            // Detail modal already handles its own quote drill-down via
            // QuoteDetailModal — but if it bubbles up here we deep-link
            // to the standalone quote page so the user keeps full nav.
            if (hid) navigate(`/quote/${hid}`);
          }}
        />
      )}
    </div>
  );
}

function Chip({ label, count, active, onClick, dot, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-sm border transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
      <span className={`text-[10px] font-bold ${active ? "text-white/70" : "text-slate-500"}`}>{count}</span>
    </button>
  );
}

function KV({ k, v, highlight }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{k}</div>
      <div className={`font-mono ${highlight || "text-slate-700"}`}>{v}</div>
    </div>
  );
}
